import styled from "styled-components";

import { DESKTOP_BREAKPOINT } from "../../components/AppShell/styles";
import { Card } from "../Links/components/LinkCard/styles";

export const Intro = styled.p`
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-neutral-150);
`;

export const List = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-content: start;
  gap: 0.75rem;
  margin: 1rem 0 2rem;
`;

/** A contact in the list: Keepo Links' card, with the sender's initial where a link has its site's icon. */
export const ContactFrame = styled(Card)`
  .initial {
    font-family: "Poppins", sans-serif;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--color-neutral-white);
  }
`;

/** The contact on top (as a domain's status card): the initial, who and when, and the ways to answer. */
export const Overview = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 1.25rem;
  align-items: center;
  padding: 1.25rem;
  font-family: Inter, sans-serif;
  background: var(--color-neutral-dark);
  border-radius: 1rem;

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    padding: 1.25rem 1.5rem;
  }

  .initial {
    display: grid;
    flex-shrink: 0;
    place-content: center;
    width: 56px;
    height: 56px;
    font-family: "Poppins", sans-serif;
    font-size: 1.375rem;
    font-weight: 600;
    color: var(--color-neutral-white);
    background: var(--color-neutral-darkness);
    border-radius: 12px;
  }

  .who {
    flex: 1;
    min-width: 0;
  }

  h3 {
    overflow: hidden;
    font-family: "Poppins", sans-serif;
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1.75rem;
    color: var(--color-neutral-white);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  p {
    margin-top: 0.125rem;
    font-size: 0.8125rem;
    line-height: 1.125rem;
    color: var(--color-neutral-150);
  }

  /* the ways to answer on a line of their own, under who sent it (lined up with the name) */
  .answer {
    display: flex;
    flex-basis: 100%;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding-left: calc(56px + 1.25rem);
  }

  /* Phones: the whole width, side by side */
  @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
    .answer {
      padding-left: 0;
    }

    .answer > * {
      flex: 1;
    }
  }
`;

/** Answering: a pill (WhatsApp in the brand's blue, as a domain's main action; e-mail outlined). */
export const ReplyButton = styled.a<{ $primary?: boolean }>`
  display: inline-flex;
  gap: 0.375rem;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 1rem;
  font-family: Inter, sans-serif;
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--color-neutral-white);
  white-space: nowrap;
  background: ${({ $primary }) => ($primary ? "var(--color-brand-blue)" : "transparent")};
  border: 1px solid ${({ $primary }) => ($primary ? "var(--color-brand-blue)" : "var(--color-neutral-base)")};
  border-radius: 100px;
  transition:
    border-color 0.15s,
    filter 0.15s;

  &:hover {
    filter: brightness(1.12);
    border-color: ${({ $primary }) => ($primary ? "var(--color-brand-blue)" : "var(--color-neutral-150)")};
  }
`;

/** A block of the screen with its title (the message, the data). */
export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

/** What the person wrote, in a frame of its own. */
export const Message = styled.p`
  padding: 1.25rem 1.5rem;
  font-family: Inter, sans-serif;
  font-size: 0.9375rem;
  line-height: 1.5rem;
  color: var(--color-neutral-white);
  white-space: pre-line;
  overflow-wrap: anywhere;
  background: var(--color-neutral-dark);
  border-radius: 1rem;
`;

/** The rest of what they filled in: one card, a row per field (its label over its value) with copy on the right. */
export const DataRows = styled.div`
  overflow: hidden;
  font-family: Inter, sans-serif;
  background: var(--color-neutral-dark);
  border-radius: 1rem;

  .row {
    display: flex;
    gap: 1rem;
    align-items: center;
    justify-content: space-between;
    padding: 0.875rem 1.25rem;
  }

  .row + .row {
    border-top: 1px solid var(--color-neutral-base);
  }

  dl {
    min-width: 0;
  }

  dt {
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--color-neutral-150);
  }

  dd {
    margin-top: 0.125rem;
    font-size: 0.9375rem;
    line-height: 1.375rem;
    color: var(--color-neutral-white);
    overflow-wrap: anywhere;
  }

  a {
    color: var(--color-neutral-white);
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .empty {
    padding: 1rem 1.25rem;
    font-size: 0.875rem;
    color: var(--color-neutral-150);
  }
`;

export const Note = styled.p`
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  color: var(--color-neutral-150);

  button {
    font-weight: 700;
    color: var(--color-neutral-white);
    text-decoration: underline;
  }
`;

export const LoadingMore = styled.div`
  display: flex;
  justify-content: center;
  padding: 0.5rem 0 2rem;
`;

export const LoadingScreen = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;

  .card {
    height: 84px;
    border-radius: 1rem;
  }
`;
